/*
 * OrbitChatDrawer — slide-in chat panel for dashboard pages.
 *
 * ONE ORBIT, TWO MOUNTS. This drawer renders the SAME <OrbitIntake>
 * component that powers the full-page /orbit/intake — in `variant="panel"`
 * — so the dashboard Orbit is identical: same directive cards, same L2
 * phase walk, same design, same logic. Status syncs for free because both
 * mounts share `orbit:turns:${clientId}` localStorage + the same backend.
 * (The old divergent OrbitChatSurface is retired from this path.)
 *
 * Listens for `window.dispatchEvent('orbit:open')` so any UI affordance
 * (e.g. "Talk to Orbit" buttons inside section drawers) can summon it.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, X, Maximize2, Minimize2 } from 'lucide-react';
import clsx from 'clsx';

import { OrbitIntake } from '../OrbitIntake';


interface OrbitChatDrawerProps {
  clientId: string;
  /** Refresh hook for the host page after each turn. */
  onAfterTurn?: () => void;
  /** Pre-seeded active field from the host context. */
  initialActiveField?: string | null;
  /** Greeting shown when the conversation is empty. */
  initialGreeting?: string | null;
  /**
   * Inquirer scope. The drawer is mounted at the page level and stays
   * mounted across tab switches; the host page passes the current tab's
   * layer so the chat never serves L1 fields while the user is reading
   * the Scoping tab (or vice-versa).
   */
  layer?: 'L1' | 'L2';
  /**
   * The host surface's intent. ONE Orbit conversation is shared by every
   * mount, so on open we assert this route — "problem" (Problems page,
   * global header) presents a fresh problem opener; "company" (My Business)
   * keeps the company map — instead of resuming wherever the other surface
   * left the active question.
   */
  route?: 'problem' | 'company';
  /**
   * Show the floating "Talk to Orbit" launcher when closed. Only My Business
   * wants it (the company-mapping chat entry); every other surface opens
   * Orbit via the global "Scope a problem" header button or a center CTA, so
   * this defaults off to avoid a third redundant button on the page.
   */
  showLauncher?: boolean;
  /** Conversation context — keeps each surface on its OWN Orbit thread:
   *  'company' (My Business), 'problem' (Scope a problem), `problem:<id>`
   *  (a specific problem). Passed straight to OrbitIntake. */
  context?: string;
  /** Start a fresh problem (first problem question) every open — for the
   *  "Scope a problem" surfaces. */
  freshProblem?: boolean;
}


export const OrbitChatDrawer: React.FC<OrbitChatDrawerProps> = ({
  clientId, onAfterTurn, initialActiveField = null, initialGreeting = null, layer, route,
  showLauncher = false, context = '', freshProblem = false,
}) => {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(false);
  // An open request can carry its OWN intent (route + context + fresh) that
  // overrides this drawer's static defaults for that open. This is what lets
  // the global "Scope a problem" button open a FRESH problem even on a page
  // whose drawer defaults to company (My Business). Pending = the current
  // open's intent; cleared on close so the launcher falls back to defaults.
  const [pendingRoute, setPendingRoute] = useState<'problem' | 'company' | null>(null);
  const [pendingFresh, setPendingFresh] = useState<boolean | null>(null);
  const [pendingContext, setPendingContext] = useState<string | null>(null);
  const [pendingSeedPrompt, setPendingSeedPrompt] = useState<string | null>(null);
  const [pendingSeedMeta, setPendingSeedMeta] = useState<{
    workRoute?: string | null;
    source?: string | null;
    provider?: string | null;
  } | null>(null);
  // Bumped on every FRESH "Scope a problem" open. Feeds the OrbitIntake `key`
  // so a fresh request REMOUNTS the chat (re-running its fresh-problem reset)
  // even when the drawer was already open — otherwise re-clicking "Scope a
  // problem" on an open panel keeps the stale question.
  const [freshNonce, setFreshNonce] = useState(0);

  // Window-level open hook — section drawers, "Talk to Orbit" buttons,
  // and other surfaces can summon the chat without prop drilling.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      
      // If we are mapping business, we MUST send fresh: false so we don't clobber
      // the existing map_business session by starting a fresh problem context.
      const isCompanyRoute = d.route === 'company' || (!d.route && route === 'company');
      const isFresh = isCompanyRoute ? false : (typeof d.fresh === 'boolean' ? d.fresh : null);
      
      setPendingRoute((d.route as 'problem' | 'company') ?? null);
      setPendingFresh(isFresh);
      setPendingContext(typeof d.context === 'string' ? d.context : null);
      setPendingSeedPrompt(typeof d.seedPrompt === 'string' ? d.seedPrompt : null);
      setPendingSeedMeta(null);
      if (isFresh) setFreshNonce(n => n + 1);  // force a clean remount
      setOpen(true);
    };
    const onOpenWithPrompt = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const text = typeof d.text === 'string' ? d.text.trim() : '';
      setPendingRoute((d.route as 'problem' | 'company') || 'problem');
      setPendingFresh(typeof d.fresh === 'boolean' ? d.fresh : true);
      setPendingContext(typeof d.context === 'string' ? d.context : 'problem');
      setPendingSeedPrompt(text || null);
      setPendingSeedMeta({
        workRoute: typeof d.workRoute === 'string' ? d.workRoute : null,
        source: typeof d.source === 'string' ? d.source : null,
        provider: typeof d.provider === 'string' ? d.provider : null,
      });
      setFreshNonce(n => n + 1);
      setOpen(true);
    };
    // Orbit sends the person somewhere when a session ends (the problem it
    // scoped, the business it mapped) — the drawer gets out of the way rather
    // than sitting open over the page they were sent to.
    const onClose = () => setOpen(false);
    window.addEventListener('orbit:open', onOpen);
    window.addEventListener('orbit:open-with-prompt', onOpenWithPrompt as EventListener);
    window.addEventListener('orbit:close', onClose);
    return () => {
      window.removeEventListener('orbit:open', onOpen);
      window.removeEventListener('orbit:open-with-prompt', onOpenWithPrompt as EventListener);
      window.removeEventListener('orbit:close', onClose);
    };
  }, []);

  // Reset per-open intent on close so the next launcher open (no event detail)
  // falls back to this drawer's static defaults.
  useEffect(() => {
    if (!open) { setPendingRoute(null); setPendingFresh(null); setPendingContext(null); setPendingSeedPrompt(null); setPendingSeedMeta(null); }
  }, [open]);

  // On every open, assert a route so the active question matches intent —
  // the open event's explicit route wins, else this drawer's default prop.
  // OrbitIntake mounts as a child here, so its listener is registered (child
  // effects fire before this parent effect) by the time we dispatch; the
  // frame's delay also covers its async state hydration.
  useEffect(() => {
    if (!open) return;
    // A specific-problem open (context 'problem:<id>') must NOT assert the
    // generic route — that fires handleRoutePick in OrbitIntake, which seeds the
    // fresh "Tell me the problem…" opener over the problem's resume. Route
    // assertion is only for the generic openers ('problem' / 'company').
    const ctxNow = pendingContext ?? context;
    if (typeof ctxNow === 'string' && ctxNow.startsWith('problem:')) return;
    const r = pendingRoute ?? route;
    if (!r) return;
    const id = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('orbit:route', { detail: { route: r } }));
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, route, pendingRoute, pendingContext, context]);

  // Render through a portal to <body>. The host page may sit inside a
  // transformed/filtered ancestor (which turns `fixed` into ancestor-relative
  // positioning — the cause of the "panel starts below the top / backdrop
  // doesn't cover the top" gap on some pages but not others). Portaling to
  // body guarantees the panel + backdrop are always viewport-relative,
  // top-to-bottom, on every page.
  return createPortal(
    <>
      {/* Launcher — floating button bottom-right when closed. Only shown
          where the host opts in (My Business → company mapping). */}
      {!open && showLauncher && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[80] inline-flex items-center gap-2 rounded-full bg-gray-900 px-4 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-brand-500 whitespace-nowrap"
          aria-label="Open Orbit chat"
        >
          <MessageSquare size={15} strokeWidth={2} />
          <span>Talk to Orbit</span>
        </button>
      )}

      {/* Drawer — slide-in panel. Wider than before to give the full
          Orbit pane room to breathe (cards, dossier pill, composer). A
          single floating close button overlays the panel; OrbitIntake
          renders its own pane chrome in panel variant. */}
      <aside
        className={clsx(
          'fixed top-0 right-0 z-[110] flex h-full w-full flex-col border-l border-gray-200 bg-white shadow-2xl transition-all duration-200',
          full ? 'max-w-none' : 'max-w-[560px]',
          open ? 'translate-x-0' : 'translate-x-full pointer-events-none',
        )}
        aria-hidden={!open}
      >
        <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setFull(f => !f)}
            className="grid h-8 w-8 place-items-center rounded-md bg-black/5 text-gray-600 hover:bg-black/10"
            aria-label={full ? 'Collapse to side panel' : 'Expand to full screen'}
            title={full ? 'Collapse to side panel' : 'Expand to full screen'}
          >
            {full ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="grid h-8 w-8 place-items-center rounded-md bg-black/5 text-gray-600 hover:bg-black/10"
            aria-label="Close Orbit"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-1 min-h-0">
          {/* Only mount when open so OrbitIntake hydrates fresh from the
              latest localStorage turns each time — keeps the panel in sync
              with whatever happened on the full-page intake. */}
          {open && (
            <OrbitIntake
              key={`${pendingContext ?? context}:${freshNonce}:${pendingSeedPrompt ? 'seed' : 'plain'}`}
              variant="panel"
              onAfterTurn={onAfterTurn}
              context={pendingContext ?? context}
              freshProblem={pendingFresh ?? freshProblem}
              expanded={full}
              seedPrompt={pendingSeedPrompt}
              seedMeta={pendingSeedMeta}
            />
          )}
        </div>
      </aside>

      {open && (
        <button
          type="button"
          aria-label="Close chat"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[105] bg-black/20 backdrop-blur-[1px]"
        />
      )}
    </>,
    document.body,
  );
};

export default OrbitChatDrawer;
